import { client } from '../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../queries/widgets';
import { normalizeAutomatedColumnsInput } from '../../../../../utils/normalizeAutomatedColumnsInput';
import {
  isSquadDynamicCell,
  SQUAD_DYNAMIC_FONDSDATEN_KEYS,
  SQUAD_TIME_INVARIANT_FONDSDATEN_KEYS,
  type SquadDynamicCell,
} from './squadFondsdatenDynamic';
import { formatSquadUntil } from './squadSrriwert';

type Entry = { key: string; config: SquadDynamicCell };

async function fetchEntries(entries: Entry[], isin: string, until: string | undefined, out: Map<string, string>) {
  if (!entries.length) return;
  try {
    const res = await client.query({
      query: GET_AUTOMATED_COLUMNS,
      variables: {
        param: {
          data: normalizeAutomatedColumnsInput([entries.map((e) => e.config)]),
          isin,
          until,
        },
      },
      fetchPolicy: 'network-only',
    });
    const row = res?.data?.getAutomatedColumns?.[0];
    if (!Array.isArray(row)) return;
    entries.forEach((entry, i) => {
      const val = row[i];
      if (val != null && String(val).trim() !== '') {
        out.set(entry.key, String(val).trim());
      }
    });
  } catch {
    // preview stays empty for failed fetches
  }
}

/**
 * Fetch live Axion values for dynamic SQUAD Fondsdaten fields.
 * Returns a map keyed by field name (wkn, kursNav, fondsvolumen, …).
 *
 * Values that never change for a share class (the WKN) are fetched without the factsheet
 * date: the delivery carries `wkn` only since 17.10.2025, so a factsheet dated earlier
 * would otherwise lose the row.
 */
export async function fetchSquadFondsdatenDynamicValues(
  fondsdaten: Record<string, unknown> | undefined | null,
  isin: string,
  until?: string | null,
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const fundIsin = String(isin || '').trim().toUpperCase();
  if (!fundIsin || !fondsdaten) return out;

  const dated: Entry[] = [];
  const timeless: Entry[] = [];
  for (const key of SQUAD_DYNAMIC_FONDSDATEN_KEYS) {
    const raw = fondsdaten[key];
    if (isSquadDynamicCell(raw) && raw.fields?.length) {
      const invariant = (SQUAD_TIME_INVARIANT_FONDSDATEN_KEYS as readonly string[]).includes(key);
      (invariant ? timeless : dated).push({ key, config: raw });
    }
  }

  await Promise.all([
    fetchEntries(dated, fundIsin, formatSquadUntil(until) || undefined, out),
    fetchEntries(timeless, fundIsin, undefined, out),
  ]);
  return out;
}
